<template>
    <enterprise-plan-contact-flow
        v-if="signupMode === 'enterprise'"
        :plan="resolvedSignupPlan"
    />
    <byo-hosting-signup-flow
        v-else-if="signupMode === 'byo'"
        :plan="resolvedSignupPlan"
        :requested-plan-slug="requestedPlanSlug"
    />
    <v-card v-else class="auth-card register-card mx-auto" max-width="1320">
        <div class="auth-card__left">
            <div class="auth-brand">
                <img id="logo-image" alt="Smartdata logo image and dashboard link."
                            rel="noreferrer" class="mx-auto px-5" src="/static/img/logos/Smartdata_dark_L.png">
            </div>

            <div class="auth-form">
                <h1 class="auth-title">{{ registrationHeading }}</h1>
                <p class="auth-subtitle">{{ registrationSubtitle }}</p>

                <v-alert
                    v-if="signupMode === 'loading'"
                    variant="outlined"
                    type="info"
                    class="mb-4"
                >
                    Loading your selected signup plan...
                </v-alert>

                <v-alert
                    v-if="signupMode === 'unavailable'"
                    variant="outlined"
                    type="error"
                    class="mb-4"
                >
                    {{ signupPlanError }}
                </v-alert>

                <template v-if="signupMode === 'standard'">
                    <v-form ref="form" @submit.prevent="submitButton">
                    <v-alert
                        v-if="registrationSuccess"
                        variant="outlined"
                        type="success"
                        class="mb-4"
                    >
                        {{ registrationSuccessMessage }}
                    </v-alert>

                    <template v-if="!registrationSuccess">
                        <v-alert
                            v-if="errors.compromisedPassword"
                            variant="outlined"
                            type="error"
                            class="mb-2"
                        >
                            Your password has been involved in a major security
                            breach and cannot be used.
                        </v-alert>
                        <v-btn
                            v-if="errors.compromisedPassword"
                            color="primary"
                            variant="text"
                            class="mb-3 px-0"
                            @click="dialog.HIBP = true"
                        >
                            More Info
                        </v-btn>

                        <v-row class="ma-0">
                            <v-col cols="12" class="pa-0">
                                <v-text-field
                                    v-model="user.email"
                                    autofocus
                                    :rules="[rules.email]"
                                    :counter="254"
                                    label="Email Address"
                                    prepend-inner-icon="$vuetify.icons.email"
                                    :error-messages="errors.email"
                                    :disabled="emailDisabled"
                                    variant="outlined"
                                    class="auth-input"
                                    autocomplete="email"
                                    @change="errors.email = []"
                                />
                            </v-col>

                            <v-col cols="12" md="6" class="pa-0 pe-md-2">
                                <v-text-field
                                    v-model="user.firstName"
                                    :rules="[rules.required]"
                                    :counter="25"
                                    label="First Name"
                                    prepend-inner-icon="$vuetify.icons.profile"
                                    :error-messages="errors.first_name"
                                    variant="outlined"
                                    class="auth-input"
                                    autocomplete="given-name"
                                    @change="errors.first_name = []"
                                />
                            </v-col>

                            <v-col cols="12" md="6" class="pa-0 ps-md-2">
                                <v-text-field
                                    v-model="user.lastName"
                                    :rules="[rules.required]"
                                    :counter="25"
                                    label="Last Name"
                                    prepend-inner-icon="$vuetify.icons.profile"
                                    :error-messages="errors.last_name"
                                    variant="outlined"
                                    class="auth-input"
                                    autocomplete="family-name"
                                    @change="errors.last_name = []"
                                />
                            </v-col>

                            <v-col cols="12" md="5" class="pa-0 pe-md-2">
                                <v-autocomplete
                                    v-model="user.countryCode"
                                    label="Country Code"
                                    required
                                    :rules="[rules.required]"
                                    :items="dedupedCountryCodeArray"
                                    item-title="country"
                                    item-value="code"
                                    prepend-inner-icon="$vuetify.icons.phone"
                                    autocomplete="off"
                                    variant="outlined"
                                    class="auth-input"
                                />
                            </v-col>

                            <v-col cols="12" md="7" class="pa-0 ps-md-2">
                                <v-text-field
                                    v-model="user.phoneNumber"
                                    :rules="rules.phoneNumber"
                                    :counter="10"
                                    label="Phone Number"
                                    prepend-inner-icon="$vuetify.icons.mobile"
                                    variant="outlined"
                                    class="auth-input"
                                    autocomplete="tel"
                                />
                            </v-col>

                            <v-col cols="12" class="pa-0">
                                <v-text-field
                                    v-model="user.password1"
                                    :rules="[rules.required]"
                                    label="Password"
                                    prepend-inner-icon="$vuetify.icons.lock"
                                    :append-inner-icon="
                                        showPassword
                                            ? '$vuetify.icons.eye'
                                            : '$vuetify.icons.eyeSlash'
                                    "
                                    :error-messages="errors.password1"
                                    hint="Password must be at least 8 chars and not commonly used."
                                    :type="showPassword ? 'text' : 'password'"
                                    variant="outlined"
                                    class="auth-input"
                                    autocomplete="new-password"
                                    @click:append-inner="showPassword = !showPassword"
                                />
                            </v-col>

                            <v-col cols="12" class="pa-0">
                                <v-text-field
                                    v-model="user.password2"
                                    :rules="[rules.required]"
                                    label="Confirm Password"
                                    :type="showPassword ? 'text' : 'password'"
                                    :append-inner-icon="
                                        showPassword
                                            ? '$vuetify.icons.eye'
                                            : '$vuetify.icons.eyeSlash'
                                    "
                                    :error-messages="errors.password2"
                                    prepend-inner-icon="$vuetify.icons.lock"
                                    variant="outlined"
                                    class="auth-input"
                                    autocomplete="new-password"
                                    @click:append-inner="showPassword = !showPassword"
                                />
                            </v-col>

                            <v-col cols="12" class="pa-0">
                                <v-checkbox
                                    v-model="terms_checkbox"
                                    :rules="[rules.mustBeTrue]"
                                    :error="errors.terms_checkbox"
                                    class="auth-terms"
                                >
                                    <template #label>
                                        <div>
                                            I have read and agree to the
                                            <a
                                                target="_blank"
                                                href="http://static.app.ndsmartdata.com/other-assets/Smartdata-Terms-And-Conditions.pdf"
                                                @click.stop
                                            >
                                                Smartdata Usage Terms
                                            </a>
                                        </div>
                                    </template>
                                </v-checkbox>
                            </v-col>
                        </v-row>

                        <v-btn
                            size="large"
                            color="primary"
                            :loading="loader"
                            class="auth-submit mt-2"
                            @click="submitButton"
                        >
                            Register
                            <v-icon end>$vuetify.icons.register</v-icon>
                        </v-btn>
                    </template>
                    </v-form>
                </template>
            </div>
        </div>

        <div class="auth-card__right">
            <div class="auth-illustration-wrap">
                <img
                    src="/static/img/smartdata-mockup-transparent.png"
                    alt="Smartdata platform illustration"
                    class="auth-illustration"
                />
            </div>
        </div>

        <v-dialog v-model="dialog.HIBP" width="500">
            <v-card>
                <v-card-title class="text-h6">Why do we use HIBP?</v-card-title>
                <v-card-text class="pt-5">
                    <p>
                        We check passwords with "Have I Been Pwned" (HIBP) to
                        reduce account compromise risk from known data breaches.
                    </p>
                    <p>
                        Your password is checked via hash-based lookup, not sent
                        as plain text.
                    </p>
                </v-card-text>
                <v-divider />
                <v-card-actions>
                    <v-spacer />
                    <v-btn color="primary" variant="text" @click="dialog.HIBP = false">
                        Close
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="dialog.phone" width="500">
            <verification
                :email="user.email"
                :phone-number="user.phoneNumber"
                :country-code="user.countryCode"
                @phone-verified="phoneVerified"
                @close="dialog.phone = false"
            />
        </v-dialog>
    </v-card>
</template>

<script>
import axios from 'axios';
import ByoHostingSignupFlow from 'components/Profile/Register/BYOHostingSignupFlow.vue';
import EnterprisePlanContactFlow from 'components/Profile/Register/EnterprisePlanContactFlow.vue';
import countriesList from 'components/Profile/PhoneNumber/countryCodes';
import Verification from 'components/Profile/PhoneNumber/RegisterPhoneVerification.vue';

export default {
    name: 'Register',
    components: {
        ByoHostingSignupFlow,
        EnterprisePlanContactFlow,
        Verification
    },
    props: {
        dark: {
            type: Boolean,
            default: false
        }
    },
    data() {
        const urlParameters = new URLSearchParams(window.location.search);
        const requestedPlan = urlParameters.get('plan');
        return {
            user: {
                firstName: null,
                lastName: null,
                email: null,
                password1: null,
                password2: null,
                phoneNumber: null,
                countryCode: null,
                authyId: null
            },
            rules: {
                mustBeTrue: value =>
                    value === true || 'You must accept the terms and conditions.',
                required: value => !!value || 'Required field',
                email: value => {
                    const pattern = /^(([^\s"(),.:;<>@[\\\]]+(\.[^\s"(),.:;<>@[\\\]]+)*)|(".+"))@((\[(?:\d{1,3}\.){3}\d{1,3}])|(([\d-A-Za-z]+\.)+[A-Za-z]{2,}))$/;
                    return pattern.test(value) || 'Must be a valid e-mail';
                },
                phoneNumber: [
                    value => !!value || 'Required field',
                    value => /^\d*$/.test(value) || 'Numbers only.',
                    value =>
                        !value ||
                        value.length <= 10 ||
                        'Phone number cannot be longer than 10 numbers'
                ]
            },
            errors: {
                terms_checkbox: false,
                compromisedPassword: false,
                invalidFormFields: false,
                email: [],
                first_name: [],
                last_name: [],
                password1: [],
                password2: []
            },
            terms_checkbox: false,
            dialog: {
                HIBP: false,
                phone: false
            },
            showPassword: false,
            registrationSuccess: false,
            registrationError: false,
            errorMessage: null,
            loader: false,
            countryCodeArray: countriesList(),
            emailDisabled: false,
            signupMode: requestedPlan ? 'loading' : 'standard',
            requestedPlanSlug: '',
            resolvedSignupPlan: null,
            signupPlanError: ''
        };
    },
    computed: {
        registrationHeading() {
            return 'Create your account';
        },
        registrationSubtitle() {
            return 'Join Smartdata and start monitoring.';
        },
        registrationSuccessMessage() {
            return 'Your account has been successfully registered. You must verify your email before you can login. Please check your email.';
        },
        dedupedCountryCodeArray() {
            const seen = new Set();
            return this.countryCodeArray.filter((entry) => {
                if (seen.has(entry.code)) return false;
                seen.add(entry.code);
                return true;
            });
        }
    },
    mounted() {
        const urlParameters = new URLSearchParams(window.location.search);
        const requestedPlan = urlParameters.get('plan');
        const email = urlParameters.get('email');
        if (email) {
            this.emailDisabled = true;
            this.user.email = email;
        }
        if (requestedPlan) {
            this.requestedPlanSlug = requestedPlan.trim().toLowerCase();
            this.loadSignupPlan();
        } else {
            this.signupMode = 'standard';
        }
    },
    methods: {
        async loadSignupPlan() {
            this.signupMode = 'loading';
            this.signupPlanError = '';

            try {
                const response = await axios.get('/api/accounts/dataset-pass/plan/', {
                    params: {
                        plan: this.requestedPlanSlug
                    }
                });

                this.resolvedSignupPlan = response.data.plan;
                if (response.data.plan?.is_enterprise) {
                    this.signupMode = 'enterprise';
                    return;
                }

                if (response.data.plan?.byo_hosting?.is_byo_hosting_plan) {
                    this.signupMode = 'byo';
                    return;
                }

                this.requestedPlanSlug = '';
                this.signupMode = 'standard';
            } catch (error) {
                this.resolvedSignupPlan = null;
                this.signupMode = 'unavailable';
                this.signupPlanError =
                    error?.response?.data?.message ||
                    'This BYO hosting plan is currently unavailable. Please contact support.';
            }
        },
        async validateForm() {
            const validationResult = await this.$refs.form.validate();
            if (typeof validationResult === 'boolean') {
                return validationResult;
            }
            if (
                validationResult &&
                typeof validationResult === 'object' &&
                'valid' in validationResult
            ) {
                return Boolean(validationResult.valid);
            }
            return false;
        },
        async submitButton() {
            const isValid = await this.validateForm();
            if (!this.terms_checkbox || !isValid) {
                this.errors.terms_checkbox = !this.terms_checkbox;
                return;
            }

            this.errors.invalidFormFields = false;
            this.errors.compromisedPassword = false;
            this.submitRegistration();
        },
        phoneVerified(authyId) {
            this.dialog.phone = false;
            this.user.authyId = authyId;
            this.submitRegistration();
        },
        submitRegistration() {
            this.loader = true;
            const postData = {
                first_name: this.user.firstName,
                last_name: this.user.lastName,
                email: this.user.email,
                password1: this.user.password1,
                password2: this.user.password2,
                phoneNumber: this.user.phoneNumber,
                countryCode: this.user.countryCode,
                authyId: this.user.authyId,
                requestedPlanSlug:
                    this.signupMode === 'byo' ? this.requestedPlanSlug : ''
            };

            axios
                .post('/api/accounts/register/', postData)
                .then(response => response.data)
                .then(data => {
                    this.loader = false;
                    if (data.success) {
                        this.registrationError = false;
                        this.registrationSuccess = true;
                        return;
                    }

                    this.errors.email = [];
                    this.errors.first_name = [];
                    this.errors.last_name = [];
                    this.errors.password1 = [];
                    this.errors.password2 = [];

                    const errors = JSON.parse(data.errors);
                    Object.keys(errors).forEach(key => {
                        this.errors[key] = errors[key].map(error => error.message);
                    });

                    if (
                        this.errors.password2.includes(
                            'Your password has been involved in a major security breach and cannot be used.'
                        )
                    ) {
                        this.errors.compromisedPassword = true;
                    }
                })
                .catch(error => {
                    this.loader = false;
                    this.registrationError = true;
                    this.errorMessage = `There was an unknown error registering your account. ${error}`;
                    throw error;
                });
        }
    }
};
</script>

<style scoped>
.auth-card {
    display: grid;
    grid-template-columns: 1fr 1.05fr;
    border-radius: 22px !important;
    overflow: hidden;
    box-shadow: 0 24px 48px rgba(20, 29, 71, 0.14) !important;
}

.auth-card__left {
    background: #f6f8fc;
    padding: 36px 44px;
}

.auth-brand {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    color: #1ba0e6;
    margin-bottom: 16px;
}

.auth-brand__mark {
    width: 24px;
    height: 24px;
    border-radius: 6px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-weight: 700;
    background: #1ba0e6;
}

.auth-brand__name {
    font-size: 1.45rem;
    font-weight: 600;
    color: #1092d5;
}

.auth-form {
    max-width: 560px;
}

.auth-title {
    font-size: clamp(2rem, 2.6vw, 2.8rem);
    line-height: 1.08;
    margin-bottom: 6px;
    color: #1e2564;
}

.auth-subtitle {
    color: #55608f;
    font-size: 1.05rem;
    margin-bottom: 18px;
}

.auth-input {
    margin-bottom: 8px;
}

.auth-input :deep(.v-field) {
    background: #ffffff;
    border-radius: 12px;
}

.auth-terms {
    text-align: left;
}

.auth-submit {
    min-width: 180px;
    height: 50px;
    border-radius: 14px !important;
    letter-spacing: 0.02em;
    text-transform: none;
    font-size: 1.06rem !important;
}

.auth-card__right {
    position: relative;
    background: linear-gradient(160deg, #4e4fe7 0%, #38a9e6 55%, #37b6d8 100%);
    display: flex;
    align-items: flex-end;
    justify-content: center;
    padding: 30px 14px 0;
}

.auth-card__right::before {
    content: '';
    position: absolute;
    inset: 0;
    background:
        radial-gradient(circle at 22% 18%, rgba(255, 255, 255, 0.25), transparent 42%),
        linear-gradient(180deg, rgba(255, 255, 255, 0.06), rgba(255, 255, 255, 0));
}

.auth-illustration-wrap {
    position: relative;
    z-index: 1;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: flex-end;
    justify-content: center;
}

.auth-illustration {
    width: min(100%, 760px);
    max-height: 100%;
    object-fit: contain;
    transform: translateX(-4%);
    filter: drop-shadow(0 14px 20px rgba(8, 24, 72, 0.3));
}

@media (max-width: 1200px) {
    .auth-card {
        grid-template-columns: 1fr;
    }

    .auth-card__right {
        min-height: 280px;
        padding-top: 12px;
    }

    .auth-illustration {
        transform: none;
        width: min(92%, 560px);
    }
}

@media (max-width: 700px) {
    .auth-card__left {
        padding: 24px 18px;
    }

    .auth-card__right {
        min-height: 220px;
    }
}
</style>
